import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { getShortApproxDurationFormat, SECOND_MS } from "--root/utils/datetime";

import { getAvatarSize } from "--components/theme";

import { usePrecisionInterval } from "--client/common/hooks/usePrecisionInterval";

import CircularProgress, { CircularProgressBaseProps } from "--floponents-web/atoms/Progress/CircularProgress";
import { FitText } from "../FitText/FitText";

const StyledCircularTimer = styled.div`
    display: inline-block;
    position: relative;
`;
const CircularTimerContent = styled.div<{ size: number }>`
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 0;
    left: 0;
    width: ${props => props.size}px;
    height: ${props => props.size}px;
`;
const CircularTimerProgress = styled(CircularProgress)`
    
`;
const CircularTimerText = styled(FitText)`
    ${props => props.theme.typography.body1};
    align-items: center;
    color: ${props => props.theme.palette.text.primary};
    display: flex;
    height: 50%;
    margin: initial;
    position: absolute;
    width: 50%;
`;

export interface CircularTimerProps extends CircularProgressBaseProps {
    startTimestamp: number;
    endTimestamp: number;
}
export const CircularTimer: React.FC<CircularTimerProps> = props => {
    const { startTimestamp, endTimestamp, children, ...circularProgressProps } = props;

    const size = getAvatarSize(props.size);

    const startDate = useMemo(() => new Date(startTimestamp), [startTimestamp]);
    const endDate = useMemo(() => new Date(endTimestamp), [endTimestamp]);

    const [timerText, setTimerText] = useState<string>("∞");
    const updateTime = useCallback(() => {
        setTimerText(getShortApproxDurationFormat(Math.max(endTimestamp - Date.now(), 0)));
    }, [endTimestamp]);

    const [isRunning, setIsRunning] = useState(false);
    useEffect(() => {
        updateTime();
        setIsRunning(endTimestamp > Date.now());
    }, [endTimestamp, updateTime]);

    usePrecisionInterval(() => {
        updateTime();
        if (endTimestamp <= Date.now()) setIsRunning(false);
    }, SECOND_MS, isRunning);

    return (
        <StyledCircularTimer>
            <CircularTimerProgress
                startTime={startDate}
                endTime={endDate}
                {...circularProgressProps}
            />
            <CircularTimerContent size={size}>
                <CircularTimerText text={timerText} />
                {children}
            </CircularTimerContent>
        </StyledCircularTimer>
    )
};
